import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootStateOrAny } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { BRANDS } from '../../../types/brands';
import {
  dayNames,
  formatTime,
  isPLKAutoCalculatedLateNight,
} from '../common/utils';

// Type for daypart keys
type DayPartKey =
  | 'breakfastTime'
  | 'lunchTime'
  | 'snackTime'
  | 'dinnerTime'
  | 'lateNight';

// Interface for daypart object
interface DayPart {
  key: DayPartKey;
  label: string;
}

// Interface for daypart field names
interface DayPartFieldNames {
  open: string | null;
  close: string | null;
}

// Interface for daypart data
interface DayPartData {
  na?: boolean;
  time?: string;
}

// Interface for base week hours data structure
interface BaseWeekHours {
  [dayKey: string]: Record<string, string>;
}

// Interface for restaurant entity from Redux
interface RestaurantEntity {
  brand?: BRANDS;
  Region?: string;
}

interface UseDayPartDataProps {
  weekDays: Date[];
  baseWeekHours: BaseWeekHours;
  is12HFormat: boolean;
}

export const useDayPartData = ({
  baseWeekHours,
  is12HFormat,
}: UseDayPartDataProps) => {
  const { t } = useTranslation();

  // Get restaurant data from Redux store
  const { brand, Region } =
    (useSelector(
      (state: RootStateOrAny) => state.RestaurantsReducer.entity,
    ) as RestaurantEntity) || {};

  // Dynamic daypart slots based on BaseAdjustment logic
  const dayparts = useMemo((): DayPart[] => {
    const daypartList: DayPart[] = [
      { key: 'breakfastTime', label: t('operationHours.breakfastTime') },
      { key: 'lunchTime', label: t('operationHours.lunchTime') },
      { key: 'snackTime', label: t('operationHours.snackTime') },
      { key: 'dinnerTime', label: t('operationHours.dinnerTime') },
    ];

    // Add late night if not auto-calculated for PLK
    if (brand && Region && !isPLKAutoCalculatedLateNight(brand, Region, [])) {
      daypartList.push({
        key: 'lateNight',
        label: t('operationHours.lateNight'),
      });
    }

    return daypartList;
  }, [brand, Region, t]);

  // Helper function to get daypart field names
  const getDaypartFieldNames = (daypartKey: DayPartKey): DayPartFieldNames => {
    const fieldNames: Record<DayPartKey, DayPartFieldNames> = {
      breakfastTime: { open: 'breakfastTimeOpen', close: 'breakfastTimeClose' },
      lunchTime: { open: 'lunchTimeOpen', close: 'lunchTimeClose' },
      snackTime: { open: 'snackTimeOpen', close: 'snackTimeClose' },
      dinnerTime: { open: 'dinnerTimeOpen', close: 'dinnerTimeClose' },
      lateNight: { open: 'lateNightTimeOpen', close: 'lateNightTimeClose' },
    };

    return fieldNames[daypartKey] || { open: null, close: null };
  };

  // Get daypart data for a specific day and daypart
  const getDaypartData = (dayIndex: number, daypart: string): DayPartData => {
    // Map Monday-Sunday display order to the baseWeekHours format (1-Sunday, 2-Monday, etc.)
    const dayNumber = dayIndex === 6 ? 1 : dayIndex + 2; // Sunday (index 6) is 1, Monday (index 0) is 2, etc.
    const dayKey = `${dayNumber} - ${dayNames[dayIndex]}`;
    const dayData = baseWeekHours[dayKey];

    if (!dayData) {
      return { na: true };
    }

    const daypartObj = dayparts.find(s => s.label === daypart);
    if (!daypartObj) {
      return { na: true };
    }

    const daypartKey = daypartObj.key;
    const fieldNames = getDaypartFieldNames(daypartKey);

    if (!fieldNames.open || !fieldNames.close) {
      return { na: true };
    }

    const openTime = dayData[fieldNames.open];
    const closeTime = dayData[fieldNames.close];

    if (!openTime && !closeTime) {
      return { na: true };
    }

    const formattedOpen = openTime ? formatTime(openTime, is12HFormat) : null;
    const formattedClose = closeTime
      ? formatTime(closeTime, is12HFormat)
      : null;

    if (formattedOpen && formattedClose) {
      return { time: `${formattedOpen} - ${formattedClose}` };
    } else if (formattedOpen) {
      return { time: `${formattedOpen} - ${t('app.common.na')}` };
    } else if (formattedClose) {
      return { time: `${t('app.common.na')} - ${formattedClose}` };
    }

    return { na: true };
  };

  return {
    dayparts,
    getDaypartData,
  };
};

export type { DayPartData, DayPart, DayPartKey, BaseWeekHours };
